import React, { useState } from 'react';
import { Braces } from 'lucide-react';
import { SecondaryButton } from 'src/shared/components/button/Button';
import DocumentEditor from './DocumentEditor';

type DocumentModesProps = {
    /** What is being edited, for the wording: `version` or `dataset`. */
    noun: string;
    /** What the form holds right now, as a document. Read each time the hatch is opened. */
    buildDocument: () => unknown;
    /** Takes an edited document back into the form. */
    onApply: (document: Record<string, unknown>) => void;
    /** The form. */
    children: React.ReactNode;
};

/**
 * The form, with a way to flip it open and edit the raw document.
 *
 * Not two tabs: the form is the thing being edited, and JSON is a hatch into the same edit.
 * Opening it reads whatever the form holds *now*, so nothing typed is lost on the way in;
 * applying hands the document back to the form, so nothing is saved that the form has not
 * checked; cancelling leaves the form exactly as it was.
 *
 * The same hatch on a dataset and on a version, because they are the same act — and because a
 * reader who has learnt one has learnt the other.
 */
const DocumentModes = ({ noun, buildDocument, onApply, children }: DocumentModesProps) => {
    // The document as it was when the hatch opened. Held rather than recomputed, so a render
    // while the editor is open cannot pull what is being typed out from under it.
    const [opened, setOpened] = useState<unknown | null>(null);

    if (opened !== null) {
        return (
            <DocumentEditor
                noun={noun}
                document={opened}
                onApply={document => {
                    onApply(document);
                    setOpened(null);
                }}
                onCancel={() => setOpened(null)}
            />
        );
    }

    return (
        <div className='flex flex-col gap-4'>
            {/* A bordered button, not a bare label: this opens a different way of editing the
                same thing, which is an action rather than a link. */}
            <div className='flex w-full max-w-3xl justify-end'>
                <SecondaryButton type='button' onClick={() => setOpened(buildDocument())} data-testid='document-edit-as-json'>
                    <Braces className='mr-2 h-4 w-4' aria-hidden='true' />
                    Edit as JSON
                </SecondaryButton>
            </div>

            {children}
        </div>
    );
};

export default DocumentModes;
